export const RUNTIME_SCOPE_LIVE_CAMPAIGN = 'liveCampaign';
export const RUNTIME_SCOPE_CAMPAIGN_SANDBOX = 'campaignSandbox';
export const RUNTIME_SCOPE_LOCAL_DEMO = 'localDemo';

export const STORAGE_MODE_LOCAL_SANDBOX = 'localSandbox';
export const STORAGE_MODE_LOCAL_DEMO = 'localDemo';

export const CAMPAIGN_SANDBOX_LAUNCH_TYPE = 'campaignSandbox';
export const CAMPAIGN_SANDBOX_QUERY_PARAM = 'campaignSandbox';

export const CAMPAIGN_SANDBOX_ROLE_DM = 'dm';
export const CAMPAIGN_SANDBOX_ROLE_PLAYER = 'player';

export const CAMPAIGN_SANDBOX_SOURCE_BLANK = 'blank';
export const CAMPAIGN_SANDBOX_SOURCE_DEMO = 'demo';
export const CAMPAIGN_SANDBOX_SOURCE_CAMPAIGN = 'campaign';
export const CAMPAIGN_SANDBOX_SOURCE_FORGE_PREVIEW = 'forgePreview';

const DEFAULT_SANDBOX_ID = 'default';

// Canonical sandbox-id slug. Every module that builds sandbox-scoped ids
// (storage keys, character-id prefixes, char-row matching) MUST use this one
// normalization so ids always compare equal across layers.
export const normalizeSandboxSegment = (value, fallback = 'sandbox') => {
  const raw = typeof value === 'string' || typeof value === 'number'
    ? String(value).trim().toLowerCase()
    : '';
  const segment = raw
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return segment || fallback;
};

export const normalizeCampaignSandboxSeedId = (sandboxId = DEFAULT_SANDBOX_ID) => (
  normalizeSandboxSegment(sandboxId, DEFAULT_SANDBOX_ID)
);

export const buildSandboxCampaignId = (sandboxId = DEFAULT_SANDBOX_ID) => (
  `sandbox-campaign-${normalizeCampaignSandboxSeedId(sandboxId)}`
);

// Live-authority fields that must never ride into a sandbox. Applied deeply
// when cloning live characters into a sandbox seed (campaignSandboxSeedBuilders)
// and top-level on every sandbox char write/load (createLocalDriver,
// campaignSandboxStorage). ONE list — do not fork per-module copies; a field
// added in only one place is a silent leak.
export const SANDBOX_AUTHORITY_FIELD_KEYS = Object.freeze([
  '_id',
  '_creationTime',
  'ownerUserId',
  'dmUserId',
  'addedByUserId',
  'createdByUserId',
  'updatedByUserId',
  'viewerUserId',
  'userId',
  'ownerEmail',
  'membershipId',
  'campaignMembershipId',
  'inviteId',
  'viewerSelectionId',
  'activeCharId',
  'viewerCharId',
  'sourceCampaignId',
  'originSourceCampaignId',
  'rulesSourceCampaignId',
  'testPromotedAt',
  'testPromotedByUserId',
  'convertedFromTestAt',
  'convertedFromTestByUserId',
  'testDataAppliedAt',
  'isTestCharacter',
  'isPromotedTestCharacter',
  'testPresetKey',
  'testPresetCategories',
  'password',
  'dmNotes',
  'secretNotes',
  'privateNotes',
  'gmNotes',
  'isMine',
  'isPlayable',
  'players',
  'friends',
]);

// Superset used when normalizing a sandbox character row: the authority
// fields above PLUS identity fields the normalizer re-stamps itself
// (id/campaignId/sandboxId markers) so stale live values can't survive.
export const SANDBOX_CHARACTER_AUTHORITY_FIELDS = Object.freeze([
  ...SANDBOX_AUTHORITY_FIELD_KEYS,
  'campaignId',
  'campaignStatus',
  'scopeKind',
  'sourceCharId',
  'charId',
  'clonedAt',
]);

const CHARACTER_AUTHORITY_FIELD_SET = new Set(SANDBOX_CHARACTER_AUTHORITY_FIELDS);

export const stripSandboxCharacterAuthorityFields = (char = {}) => (
  Object.fromEntries(
    Object.entries(char || {})
      .filter(([key]) => !CHARACTER_AUTHORITY_FIELD_SET.has(key)),
  )
);

// A char row counts as belonging to `sandboxId` only if it carries an explicit
// sandbox marker AND (when it names a sandbox) names this one. Rows without
// markers are dropped by normalizeSandboxStateChars — a hand-edited snapshot
// can't smuggle live rows into a sandbox session.
export const isSandboxCharacterForSandbox = (char, sandboxId, fallbackId = '') => {
  if (!char || typeof char !== 'object') return false;
  const normalizedSandboxId = normalizeCampaignSandboxSeedId(sandboxId);
  const sandboxPrefix = `sandbox-${normalizedSandboxId}-`;
  const candidateId = String(char.id || char.charId || fallbackId || '').toLowerCase();
  const explicitSandboxId = typeof char.sandboxId === 'string' ? char.sandboxId.trim() : '';
  const hasSandboxMarker = char.isSandboxCharacter === true
    || char.campaignCharacterKind === 'sandbox'
    || candidateId.startsWith(sandboxPrefix);
  if (!hasSandboxMarker) return false;
  if (explicitSandboxId) return normalizeCampaignSandboxSeedId(explicitSandboxId) === normalizedSandboxId;
  return candidateId.startsWith(sandboxPrefix);
};

// Pure normalization of a sandbox state's `chars` collection: keep only rows
// that belong to this sandbox, strip live-authority fields, re-stamp identity.
// Used at load time (campaignSandboxStorage), import time, and write time
// (createLocalDriver) so every consumer sees the same clean shape.
export const normalizeSandboxStateChars = (chars = {}, sandboxId, campaignId = null) => {
  if (!chars || typeof chars !== 'object') return {};
  const normalizedSandboxId = normalizeCampaignSandboxSeedId(sandboxId);
  // Only accept a caller-provided campaign id if it's sandbox-shaped — a
  // hand-edited snapshot must not be able to re-stamp chars with a live
  // campaign id via globalSettings.campaignId.
  const sandboxCampaignId = typeof campaignId === 'string' && campaignId.startsWith('sandbox-campaign-')
    ? campaignId
    : buildSandboxCampaignId(normalizedSandboxId);
  const next = {};
  Object.entries(chars).forEach(([entryId, char]) => {
    if (!isSandboxCharacterForSandbox(char, normalizedSandboxId, entryId)) return;
    const charId = String(char.id || char.charId || entryId || '').trim();
    if (!charId) return;
    next[charId] = {
      ...stripSandboxCharacterAuthorityFields(char),
      id: charId,
      campaignId: sandboxCampaignId,
      sandboxId: normalizedSandboxId,
      isSandboxCharacter: true,
      campaignCharacterKind: 'sandbox',
    };
  });
  return next;
};

export const normalizeCampaignSandboxRole = (role) => (
  role === CAMPAIGN_SANDBOX_ROLE_PLAYER
    ? CAMPAIGN_SANDBOX_ROLE_PLAYER
    : CAMPAIGN_SANDBOX_ROLE_DM
);

export const normalizeCampaignSandboxSourceMode = (sourceMode) => {
  if (
    sourceMode === CAMPAIGN_SANDBOX_SOURCE_DEMO
    || sourceMode === CAMPAIGN_SANDBOX_SOURCE_CAMPAIGN
    || sourceMode === CAMPAIGN_SANDBOX_SOURCE_FORGE_PREVIEW
  ) {
    return sourceMode;
  }
  return CAMPAIGN_SANDBOX_SOURCE_BLANK;
};

export const buildCampaignSandboxLaunchTarget = ({
  sandboxId = DEFAULT_SANDBOX_ID,
  sourceCampaignId = null,
  sourceMode = CAMPAIGN_SANDBOX_SOURCE_BLANK,
  sandboxRole = CAMPAIGN_SANDBOX_ROLE_DM,
  sandboxPersonaId = null,
  initialView = null,
  enabledFeatures,
  homebrew,
} = {}) => {
  const role = normalizeCampaignSandboxRole(sandboxRole);
  const target = {
    type: CAMPAIGN_SANDBOX_LAUNCH_TYPE,
    runtimeScope: RUNTIME_SCOPE_CAMPAIGN_SANDBOX,
    storageMode: STORAGE_MODE_LOCAL_SANDBOX,
    forceLocalStorage: true,
    waitForContext: false,
    sandboxId: sandboxId || DEFAULT_SANDBOX_ID,
    sourceCampaignId: sourceCampaignId || null,
    sourceMode: normalizeCampaignSandboxSourceMode(sourceMode),
    sandboxRole: role,
    sandboxPersonaId: role === CAMPAIGN_SANDBOX_ROLE_PLAYER ? (sandboxPersonaId || null) : null,
  };
  if (initialView) target.initialView = initialView;
  if (enabledFeatures !== undefined) target.enabledFeatures = enabledFeatures || null;
  if (homebrew !== undefined) target.homebrew = homebrew || null;
  return target;
};

export const isCampaignSandboxLaunchTarget = (target) => (
  target?.type === CAMPAIGN_SANDBOX_LAUNCH_TYPE
  || target?.runtimeScope === RUNTIME_SCOPE_CAMPAIGN_SANDBOX
  || target?.storageMode === STORAGE_MODE_LOCAL_SANDBOX
);

export const buildCampaignSandboxInitialLaunch = (target) => {
  if (!isCampaignSandboxLaunchTarget(target)) return null;
  const role = normalizeCampaignSandboxRole(target.sandboxRole || target.role);
  const base = role === CAMPAIGN_SANDBOX_ROLE_PLAYER
    ? { role, charId: target.sandboxPersonaId || target.charId || null }
    : { role: CAMPAIGN_SANDBOX_ROLE_DM };
  const launch = {
    ...base,
    runtimeScope: RUNTIME_SCOPE_CAMPAIGN_SANDBOX,
    storageMode: STORAGE_MODE_LOCAL_SANDBOX,
    sandboxId: target.sandboxId || DEFAULT_SANDBOX_ID,
    sourceCampaignId: target.sourceCampaignId || null,
    sourceMode: normalizeCampaignSandboxSourceMode(target.sourceMode),
  };
  if (target.initialView) launch.initialView = target.initialView;
  if (target.enabledFeatures !== undefined) launch.enabledFeatures = target.enabledFeatures || null;
  if (target.homebrew !== undefined) launch.homebrew = target.homebrew || null;
  return launch;
};

export const buildCampaignSandboxAppKey = (target) => {
  if (!isCampaignSandboxLaunchTarget(target)) return null;
  const role = normalizeCampaignSandboxRole(target.sandboxRole || target.role);
  const persona = role === CAMPAIGN_SANDBOX_ROLE_PLAYER
    ? (target.sandboxPersonaId || target.charId || 'player')
    : CAMPAIGN_SANDBOX_ROLE_DM;
  return `sandbox:${target.sandboxId || DEFAULT_SANDBOX_ID}:${role}:${persona}`;
};
